const base = () => process.env.REACT_APP_BACKEND_URL;

const auth = (token, extra = {}) => ({ Authorization: `Bearer ${token}`, ...extra });

export const derwentApi = {
  status: (token) => fetch(`${base()}/api/derwent/status`, { headers: auth(token) }).then((r) => r.json()),
  history: (token, sid) => fetch(`${base()}/api/derwent/chat/history/${sid}`, { headers: auth(token) }).then((r) => (r.ok ? r.json() : [])),
  clear: (token, sid) => fetch(`${base()}/api/derwent/chat/history/${sid}`, { method: 'DELETE', headers: auth(token) }),
  sessions: (token) => fetch(`${base()}/api/derwent/sessions`, { headers: auth(token) }).then((r) => (r.ok ? r.json() : [])),
  getEngine: (token) => fetch(`${base()}/api/derwent/settings/engine`, { headers: auth(token) }).then((r) => (r.ok ? r.json() : null)),
  saveEngine: (token, engine) => fetch(`${base()}/api/derwent/settings/engine`, {
    method: 'PUT', headers: auth(token, { 'Content-Type': 'application/json' }), body: JSON.stringify({ engine }),
  }).then((r) => (r.ok ? r.json() : null)),
  stream: (token, body, signal) => fetch(`${base()}/api/derwent/chat-stream`, {
    method: 'POST', headers: auth(token, { 'Content-Type': 'application/json' }), body: JSON.stringify(body), signal,
  }),
  confirmAction: (token, id) => fetch(`${base()}/api/derwent/actions/${id}/confirm`, { method: 'POST', headers: auth(token) }).then(async (r) => ({ ok: r.ok, ...(await r.json().catch(() => ({}))) })),
  cancelAction: (token, id) => fetch(`${base()}/api/derwent/actions/${id}/cancel`, { method: 'POST', headers: auth(token) }).then(async (r) => ({ ok: r.ok, ...(await r.json().catch(() => ({}))) })),
};

// Minimal SSE parser over a fetch body. Comment lines (": hb") surface as `ping` so the UI can reset its stall timer.
export async function* parseSse(stream) {
  const reader = stream.getReader();
  const decoder = new TextDecoder('utf-8');
  let buf = '';
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let idx;
    while ((idx = buf.indexOf('\n\n')) >= 0) {
      const raw = buf.slice(0, idx);
      buf = buf.slice(idx + 2);
      if (raw.startsWith(':')) { yield { event: 'ping', data: {} }; continue; }
      let event = 'message';
      let data = '';
      for (const ln of raw.split('\n')) {
        if (ln.startsWith('event:')) event = ln.slice(6).trim();
        else if (ln.startsWith('data:')) data += ln.slice(5).trim();
      }
      if (!data) continue;
      try { yield { event, data: JSON.parse(data) }; } catch (_e) { /* ignore malformed */ }
    }
  }
}

// Context-aware starter prompts — static so opening the assistant costs no API call.
const SUGGESTIONS = {
  home: ['Who needs my attention today?', 'Who started their portal but hasn\'t finished?', 'How many applications have we lodged this week?', 'Which clients haven\'t been contacted today?'],
  myweek: ['Plan my day', 'What should I focus on?', 'How am I tracking this week?', 'Show me what I\'m avoiding', 'What should I do first?', 'What am I waiting on?'],
  drop_drive: ['What documents are we still waiting on?', 'Find the latest payslip', 'What\'s missing from this file?', 'Summarise the income evidence'],
  client: ['Summarise this client', 'What documents are we still waiting on?', 'Add a note: tried calling, left a message', 'What should happen next?'],
  client_portal: ['Who started their portal but hasn\'t finished?', 'Who completed their portal overnight?', 'Which clients haven\'t been contacted today?', 'Show me clients ready for proposal'],
  lead_nurture: ['Who needs contacting today?', 'Which leads have gone quiet?', 'Show my highest priority leads', 'Summarise my pipeline'],
  game_plan: ['What should we focus on?', 'How are we tracking this week?', 'What files are delayed?', 'What\'s ready to lodge?'],
  credit: ['What\'s in the credit queue?', 'Which MIRs need actioning?', 'What\'s over SLA?', 'What lodged today?'],
  settlements: ['What settles this week?', 'Which settlements are at risk?', 'What approvals came through today?', 'Show settled this month'],
  post_settlement: ['Which clients have gone quiet?', 'Who has a fixed rate expiring soon?', 'What care tasks are due?', 'Show clients settled this year'],
  esign: ['What\'s waiting to be signed?', 'Which E-Signs completed today?', 'What\'s expiring soon?', 'Summarise E-Sign activity this week'],
  id_verification: ['Who hasn\'t completed ID verification?', 'What verifications came in today?', 'Which need broker review?', 'Show this week\'s verifications'],
  tasks: ['What\'s on my plate today?', 'Add a task to call Sarah tomorrow', 'What\'s overdue?', 'Who needs my attention today?'],
};
SUGGESTIONS.default = SUGGESTIONS.home;

export const suggestionsFor = (ctx = {}) => {
  if (ctx.application_id || ctx.lead_id || ctx.deal_id || ctx.client_name) return SUGGESTIONS.client;
  return SUGGESTIONS[ctx.module] || SUGGESTIONS.default;
};
